import React, { useState, isValidElement, cloneElement } from 'react';
import {
  View,
  StyleSheet,
  TextInput as RNTextInput,
  TextInputProps,
  Platform,
  Pressable,
} from 'react-native';
import Text from './Text';
import { useTheme } from '../../hooks/useTheme';

interface TextFieldProps extends TextInputProps {
  label?: string;
  error?: string;
  helperText?: string;
  multiline?: boolean;
  numberOfLines?: number;
  containerStyle?: Record<string, unknown>;
  rightIcon?: React.ReactNode;
  onRightIconPress?: () => void;
  showCharCount?: boolean;
  maxLength?: number;
}

const TextField = React.forwardRef<RNTextInput, TextFieldProps>(
  (
    {
      label,
      error,
      helperText,
      multiline = false,
      numberOfLines,
      containerStyle,
      style,
      onFocus,
      onBlur,
      rightIcon,
      onRightIconPress,
      showCharCount = false,
      maxLength,
      value,
      ...props
    },
    ref
  ) => {
    const { colors } = useTheme();
    const [isFocused, setIsFocused] = useState(false);
    const currentLength = value ? String(value).length : 0;

    const handleFocus = (
      e: Parameters<NonNullable<TextInputProps['onFocus']>>[0]
    ) => {
      setIsFocused(true);
      if (onFocus) {
        onFocus(e);
      }
    };

    const handleBlur = (
      e: Parameters<NonNullable<TextInputProps['onBlur']>>[0]
    ) => {
      setIsFocused(false);
      if (onBlur) {
        onBlur(e);
      }
    };

    const getBorderWidth = () => {
      if (isFocused && !error) return 2;
      return 1;
    };

    // Get the border color - use theme_color (purple) for focus state
    const borderColor =
      isFocused && !error
        ? colors.theme_color || colors.primary
        : error
          ? colors.errorText
          : colors.border;

    const inputStyle = [
      multiline ? styles.formTextArea : styles.formInput,
      {
        backgroundColor: colors.card,
        color: colors.text,
        borderColor: borderColor,
        borderWidth: getBorderWidth(),
        // Remove browser default outline on web
        ...(Platform.OS === 'web' && {
          outline: 'none',
          outlineWidth: 0,
          outlineColor: 'transparent',
          outlineStyle: 'none',
        }),
      },
      // Apply custom style
      style,
    ];

    return (
      <View style={[styles.container, containerStyle]}>
        {label && (
          <Text style={[styles.modalLabel, { color: colors.text }]}>
            {label}
          </Text>
        )}
        <View style={styles.inputContainer}>
          <RNTextInput
            ref={ref}
            {...props}
            value={value}
            maxLength={maxLength}
            style={[
              inputStyle,
              rightIcon ? styles.inputWithIcon : null,
              showCharCount && maxLength ? styles.inputWithCharCount : null,
            ]}
            multiline={multiline}
            numberOfLines={numberOfLines}
            placeholderTextColor={colors.secondary}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          {showCharCount && maxLength && (
            <View
              style={[
                styles.charCountContainer,
                multiline ? styles.charCountContainerMultiline : null,
              ]}
            >
              <Text style={[styles.charCount, { color: colors.secondary }]}>
                {currentLength}/{maxLength}
              </Text>
            </View>
          )}
          {rightIcon && (
            <Pressable
              style={[
                styles.iconContainer,
                Platform.OS === 'web' && styles.iconContainerWeb,
              ]}
              onPress={onRightIconPress}
              disabled={!onRightIconPress}
            >
              {({ focused }) =>
                isValidElement(rightIcon)
                  ? cloneElement(
                      rightIcon as React.ReactElement<{ color?: string }>,
                      {
                        ...(focused && {
                          color: colors.theme_color || colors.primary,
                        }),
                      }
                    )
                  : rightIcon
              }
            </Pressable>
          )}
        </View>
        {(error || helperText) && (
          <Text
            style={[
              styles.helperText,
              { color: error ? colors.errorText : colors.secondary },
            ]}
          >
            {error || helperText}
          </Text>
        )}
      </View>
    );
  }
);

TextField.displayName = 'TextField';

const styles = StyleSheet.create({
  container: {
    marginBottom: 4,
  },
  modalLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
    marginTop: 4,
  },
  inputContainer: {
    position: 'relative',
  },
  formInput: {
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    marginBottom: 4,
    minHeight: 44,
  },
  formTextArea: {
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    minHeight: 100,
    textAlignVertical: 'top',
    marginBottom: 16,
  },
  inputWithIcon: {
    paddingRight: 48,
  },
  iconContainer: {
    position: 'absolute',
    right: 12,
    paddingTop: 12, // Match input padding
    paddingBottom: 12, // Match input padding
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 24,
    zIndex: 1,
  },
  iconContainerWeb: {
    outlineWidth: 0,
    outlineColor: 'transparent',
    outlineStyle: 'solid',
  },
  helperText: {
    fontSize: 12,
    marginTop: -4,
    marginBottom: 8,
    marginLeft: 4,
  },
  inputWithCharCount: {
    paddingRight: 60,
  },
  charCountContainer: {
    position: 'absolute',
    right: 12,
    bottom: 12,
    zIndex: 1,
  },
  charCountContainerMultiline: {
    bottom: 16,
  },
  charCount: {
    fontSize: 11,
    fontWeight: '500',
  },
});

export default TextField;
